/**
 * Profiles and friend codes (docs/plans/009-friends.md, docs/SPEC.md section 6). A profile is only two choices from fixed lists, so there is no
 * free text to moderate and no way to show who someone is outside the game. Friends see a book through `projectBook`, which has no typed names.
 * The code generator takes its random bytes as an argument (the sim itself never draws randomness).
 */
import type { BookEntry, Looks } from "./storage";

/** Fixed display names. Order matters: a profile stores the index. Only ever append. */
export const DISPLAY_NAMES: readonly string[] = [
  "Bubbles",
  "Coral",
  "Finley",
  "Marina",
  "Pearl",
  "Splash",
  "Wavy",
  "Reefy",
  "Tidal",
  "Kelpie",
  "Squirt",
  "Shelly",
  "Pebble",
  "Lagoon",
  "Breezy",
  "Sandy",
  "Sunny",
  "Misty",
  "Ripple",
  "Drifter",
  "Cove",
  "Harbor",
  "Nautilus",
  "Pipsqueak",
];

export interface Avatar {
  label: string;
}

/** Fixed avatars (how each is drawn is the screen's business). Order matters: only ever append. */
export const AVATARS: readonly Avatar[] = [
  { label: "Teal wave" },
  { label: "Pink shell" },
  { label: "Yellow star" },
  { label: "Blue fish" },
  { label: "Green leaf" },
  { label: "Orange crab" },
  { label: "Purple jelly" },
  { label: "Red coral" },
  { label: "White pearl" },
  { label: "Grey stone" },
  { label: "Mint bubble" },
  { label: "Gold sun" },
];

const isIndex = (v: unknown, length: number): boolean => typeof v === "number" && Number.isInteger(v) && v >= 0 && v < length;

export function validProfileChoice(nameIndex: unknown, avatarIndex: unknown): boolean {
  return isIndex(nameIndex, DISPLAY_NAMES.length) && isIndex(avatarIndex, AVATARS.length);
}

/** 31 symbols without 0, 1, O, I and L (easy to confuse): 31^10 is about 2^49.5 possible codes. */
export const FRIEND_CODE_ALPHABET = "23456789ABCDEFGHJKMNPQRSTUVWXYZ";
export const FRIEND_CODE_LENGTH = 10;

/** Bytes from this value up are not used, so every symbol is equally likely (248 = 8 * 31). */
const UNBIASED_BELOW = 248;

export function newFriendCode(randomBytes: () => Uint8Array): string {
  let code = "";
  while (code.length < FRIEND_CODE_LENGTH) {
    for (const byte of randomBytes()) {
      if (byte >= UNBIASED_BELOW) continue;
      code += FRIEND_CODE_ALPHABET[byte % FRIEND_CODE_ALPHABET.length];
      if (code.length === FRIEND_CODE_LENGTH) break;
    }
  }
  return code;
}

export function isFriendCode(value: string): boolean {
  return value.length === FRIEND_CODE_LENGTH && [...value].every((c) => FRIEND_CODE_ALPHABET.includes(c));
}

/** What people type or paste: any case, with spaces or hyphens. */
export function normalizeFriendCode(raw: string): string {
  return raw.toUpperCase().replace(/[\s-]/g, "");
}

/** `ABCDE-23456`, easier to read and say aloud. */
export function formatFriendCode(code: string): string {
  return code.length === FRIEND_CODE_LENGTH ? `${code.slice(0, 5)}-${code.slice(5)}` : code;
}

/** What a friend may see of one surviving pet. No typed name, ever. */
export interface FriendBookEntry {
  weekId: string;
  species: string;
  points: number;
  looks: Looks;
}

export function projectBook(book: readonly BookEntry[]): FriendBookEntry[] {
  return book.map((e) => ({ weekId: e.weekId, species: e.species, points: e.points, looks: { ...e.looks } }));
}
